import React from 'react';

const SampleQuestions = ({ questions, onQuestionClick, onClose, loading = false, isOnline = true, embedded = false }) => {
  const questionCategories = questions || [];

  const renderCategoryIcon = (iconType) => {
    switch (iconType) {
      case 'chart':
        return (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'users':
        return (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'building':
        return (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'lightning':
        return (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  // If embedded mode, render without the header
  if (embedded) {
    return (
      <div className="space-y-3">
        {loading && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <div className="flex space-x-1">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
              </div>
              <span className="text-xs text-emerald-700 font-medium">Analyzing...</span>
            </div>
          </div>
        )}

        {!isOnline && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-red-700 font-medium">Offline Mode</span>
            </div>
          </div>
        )}

        {questionCategories.length > 0 ? (
          questionCategories.map((category, categoryIndex) => (
            <div 
              key={category.id || categoryIndex} 
              className="bg-white rounded-lg border border-slate-200 overflow-hidden"
              data-testid={`question-category-${categoryIndex}`}
            >
              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center space-x-2">
                  <div className={`w-6 h-6 ${category.icon_bg || 'bg-gradient-to-br from-emerald-400 to-cyan-500'} rounded-md flex items-center justify-center`}>
                    {renderCategoryIcon(category.icon_type)}
                  </div>
                  <h4 className="font-medium text-xs text-slate-700 flex-1 truncate">
                    {category.title}
                  </h4>
                  <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                    {category.questions?.length || 0}
                  </span>
                </div>
              </div>

              <div className="p-1.5">
                {(category.questions || []).map((question, questionIndex) => (
                  <button
                    key={questionIndex}
                    onClick={() => (loading || !isOnline) ? null : onQuestionClick(question)}
                    disabled={loading || !isOnline}
                    data-testid={`sample-question-${categoryIndex}-${questionIndex}`}
                    className={`w-full text-left p-2 rounded-md transition-all text-xs group ${
                      loading || !isOnline
                        ? 'opacity-50 cursor-not-allowed bg-slate-50' 
                        : 'hover:bg-emerald-50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-start space-x-2">
                      <svg className={`w-3 h-3 mt-0.5 flex-shrink-0 ${
                        loading || !isOnline ? 'text-slate-400' : 'text-slate-400 group-hover:text-emerald-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className={`leading-relaxed ${
                        loading || !isOnline ? 'text-slate-400' : 'text-slate-600 group-hover:text-slate-800'
                      }`}>
                        {question}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-6">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-xs text-slate-500">No sample queries available</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="p-4 bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center shadow-sm">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 text-sm">Sample Queries</h3>
              <p className="text-xs text-slate-500">Financial analytics questions</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="md:hidden text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-lg hover:bg-slate-100"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {loading && (
        <div className="px-4 pb-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
              </div>
              <span className="text-sm text-emerald-700 font-medium">Analyzing data...</span>
            </div>
            <p className="text-xs text-emerald-600 mt-2">Processing your financial query</p>
          </div>
        </div>
      )}

      {!isOnline && (
        <div className="p-4 mx-4 mt-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 text-sm">Offline Mode</h4>
              <p className="text-xs text-red-600 mt-1">Questions disabled until connection restored</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {questionCategories.length > 0 ? (
          questionCategories.map((category, categoryIndex) => (
            <div 
              key={category.id || categoryIndex} 
              className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden`}
              data-testid={`question-category-${categoryIndex}`}
            >
              <div className="p-4 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 ${category.icon_bg || 'bg-gradient-to-br from-emerald-400 to-cyan-500'} rounded-lg flex items-center justify-center shadow-sm`}>
                    {renderCategoryIcon(category.icon_type)}
                  </div>
                  <h4 className={`font-medium text-sm ${category.text_color || 'text-slate-800'}`}>
                    {category.title}
                  </h4>
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-full">
                    {category.questions?.length || 0}
                  </span>
                </div>
              </div>

              <div className="p-2">
                {(category.questions || []).map((question, questionIndex) => (
                  <button
                    key={questionIndex}
                    onClick={() => (loading || !isOnline) ? null : onQuestionClick(question)}
                    disabled={loading || !isOnline}
                    data-testid={`sample-question-${categoryIndex}-${questionIndex}`}
                    className={`w-full text-left p-3 rounded-lg transition-all duration-200 group border border-transparent ${
                      loading || !isOnline
                        ? 'opacity-50 cursor-not-allowed bg-slate-50' 
                        : 'hover:bg-emerald-50 hover:border-emerald-200 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center mt-0.5 transition-colors ${
                        loading || !isOnline
                          ? 'bg-slate-200' 
                          : 'bg-slate-100 group-hover:bg-emerald-100'
                      }`}>
                        {loading ? (
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse"></div>
                        ) : !isOnline ? (
                          <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728m0-12.728l12.728 12.728" />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3 text-slate-500 group-hover:text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                      <p className={`text-sm leading-relaxed flex-1 transition-colors ${
                        loading || !isOnline
                          ? 'text-slate-400' 
                          : 'text-slate-700 group-hover:text-slate-900'
                      }`}>
                        {question}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-10">
            <p className="text-slate-500">No sample queries available. Configure queries in admin panel.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SampleQuestions;
